import React, { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import ActionSheet from './ActionSheet';
import FiltersBottomSheet from './FiltersBottomSheet';
import {
  Organization,
  OrganizationsListResponse,
  getOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
} from '../../../utils/adminApiUtils';

const OrganizationsManager: React.FC = (): React.ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [actionSheetOpen, setActionSheetOpen] = useState<number | null>(null);
  const [actionSheetPosition, setActionSheetPosition] = useState<{ x: number; y: number } | undefined>(undefined);
  const [showFiltersSheet, setShowFiltersSheet] = useState(false);

  // Фильтры и пагинация
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'updated_at'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Форма
  const [formData, setFormData] = useState({ name: '' });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);

  // Загрузка организаций
  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        limit,
        offset,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      const response: OrganizationsListResponse = await getOrganizations(params);
      setOrganizations(response.organizations);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки организаций');
      enqueueSnackbar(err instanceof Error ? err.message : 'Ошибка загрузки организаций', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [limit, offset, search, sortBy, sortOrder, enqueueSnackbar]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Сброс пагинации при изменении фильтров
  useEffect(() => {
    setOffset(0);
  }, [search, sortBy, sortOrder]);

  const handleCreate = () => {
    setSelectedOrganization(null);
    setDialogMode('create');
    setFormData({ name: '' });
    setFormErrors({});
    setShowDialog(true);
  };

  const handleEdit = (org: Organization) => {
    setSelectedOrganization(org);
    setDialogMode('edit');
    setFormData({ name: org.name });
    setFormErrors({});
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setSelectedOrganization(null);
    setFormData({ name: '' });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    if (!formData.name.trim()) {
      errors.name = 'Введите название организации';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Название должно содержать минимум 2 символа';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (dialogMode === 'create') {
        await createOrganization({ name: formData.name.trim() });
        enqueueSnackbar('Организация успешно создана', { variant: 'success' });
      } else if (selectedOrganization) {
        await updateOrganization(selectedOrganization.id, { name: formData.name.trim() });
        enqueueSnackbar('Организация успешно обновлена', { variant: 'success' });
      }
      handleCloseDialog();
      fetchOrganizations();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка сохранения организации';
      setFormErrors({ submit: message });
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteOrganization(id);
      enqueueSnackbar('Организация успешно удалена', { variant: 'success' });
      // Закрываем actionSheet и сбрасываем все состояния
      setActionSheetOpen(null);
      setDeleteConfirm(null);
      setActionSheetPosition(undefined);
      // Обновляем список после удаления
      fetchOrganizations();
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Ошибка удаления организации', { variant: 'error' });
      // В случае ошибки тоже закрываем actionSheet
      setActionSheetOpen(null);
      setDeleteConfirm(null);
      setActionSheetPosition(undefined);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU');
  };

  return (
    <div className="organizations-section">
      <div className="section-header">
        <h2>Управление организациями</h2>
        <button className="button-primary" onClick={handleCreate}>
          Добавить организацию
        </button>
      </div>

      <div className="organizations-controls">
        <div className="search-filters">
          <input
            type="text"
            placeholder="Поиск по названию"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />

          <button
            type="button"
            className="filters-button-mobile"
            onClick={() => setShowFiltersSheet(true)}
          >
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Фильтры
          </button>

          <div className="filters-desktop">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'created_at' | 'updated_at')}
              className="role-filter"
            >
              <option value="name">Сортировка: Название</option>
              <option value="created_at">Сортировка: Дата создания</option>
              <option value="updated_at">Сортировка: Дата обновления</option>
            </select>

            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              className="role-filter"
            >
              <option value="asc">По возрастанию</option>
              <option value="desc">По убыванию</option>
            </select>

            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="role-filter"
            >
              <option value={10}>10 записей</option>
              <option value={25}>25 записей</option>
              <option value={50}>50 записей</option>
              <option value={100}>100 записей</option>
            </select>
          </div>
        </div>
      </div>

      <FiltersBottomSheet
        isOpen={showFiltersSheet}
        onClose={() => setShowFiltersSheet(false)}
        title="Фильтры"
      >
        <div className="filters-sheet-content">
          <div className="form-group">
            <label>Сортировка</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'created_at' | 'updated_at')}
              className="role-filter"
            >
              <option value="name">Название</option>
              <option value="created_at">Дата создания</option>
              <option value="updated_at">Дата обновления</option>
            </select>
          </div>

          <div className="form-group">
            <label>Порядок сортировки</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              className="role-filter"
            >
              <option value="asc">По возрастанию</option>
              <option value="desc">По убыванию</option>
            </select>
          </div>

          <div className="form-group">
            <label>Количество записей</label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="role-filter"
            >
              <option value={10}>10 записей</option>
              <option value={25}>25 записей</option>
              <option value={50}>50 записей</option>
              <option value={100}>100 записей</option>
            </select>
          </div>
        </div>
      </FiltersBottomSheet>

      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : error ? (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchOrganizations}>Попробовать снова</button>
        </div>
      ) : (
        <>
          <div className="users-table-container">
            <table className="admin-table users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Название</th>
                  <th>Дата создания</th>
                  <th>Дата обновления</th>
                </tr>
              </thead>
              <tbody>
                {organizations.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '40px' }}>
                      Организации не найдены
                    </td>
                  </tr>
                ) : (
                  organizations.map((org) => (
                  <tr
                    key={org.id}
                    onClick={(e) => {
                      setActionSheetPosition({
                        x: e.clientX,
                        y: e.clientY,
                      });
                      setActionSheetOpen(org.id);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{org.id}</td>
                    <td>
                      <span className="user-name">{org.name}</span>
                    </td>
                    <td>{formatDate(org.created_at)}</td>
                    <td>{formatDate(org.updated_at)}</td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="pagination pt-3">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="pagination-btn"
              >
                ← Предыдущая
              </button>
              <span className="pagination-info">
                Показано {offset + 1}-{Math.min(offset + limit, total)} из {total}
              </span>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={offset + organizations.length >= total}
                className="pagination-btn"
              >
                Следующая →
              </button>
            </div>
          )}
        </>
      )}

      {actionSheetOpen !== null && (
        <ActionSheet
          isOpen={true}
          position={actionSheetPosition}
          onClose={() => {
            setActionSheetOpen(null);
            setDeleteConfirm(null);
            setActionSheetPosition(undefined);
          }}
          options={[
            {
              label: 'Редактировать',
              action: () => {
                const org = organizations.find(o => o.id === actionSheetOpen);
                if (org) handleEdit(org);
              },
            },
            {
              label: deleteConfirm === actionSheetOpen ? 'Подтвердить удаление' : 'Удалить',
              action: async () => {
                if (deleteConfirm === actionSheetOpen) {
                  // Подтверждение удаления - вызываем handleDelete
                  await handleDelete(actionSheetOpen);
                } else {
                  // Первое нажатие - запрашиваем подтверждение, просто обновляем label
                  setDeleteConfirm(actionSheetOpen);
                }
              },
              variant: 'danger',
              shouldClose: deleteConfirm === actionSheetOpen, // Закрываем только при подтверждении удаления
            },
          ]}
        />
      )}

      {showDialog && (
        <div className="dialog-overlay" onClick={handleCloseDialog}>
          <div className="user-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>{dialogMode === 'create' ? 'Добавить организацию' : 'Редактировать организацию'}</h3>
              <button
                type="button"
                className="dialog-close-button"
                onClick={handleCloseDialog}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div className="user-dialog-content">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Название *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (formErrors.name) {
                        setFormErrors({ ...formErrors, name: '' });
                      }
                    }}
                    className={formErrors.name ? 'error' : ''}
                    placeholder="Введите название организации"
                  />
                  {formErrors.name && <div className="error-text">{formErrors.name}</div>}
                </div>

                {formErrors.submit && <div className="error-text">{formErrors.submit}</div>}
              </form>
            </div>

            <div className="dialog-actions-fixed">
              <button type="button" className="secondary" onClick={handleCloseDialog}>
                Отмена
              </button>
              <button
                type="button"
                className="primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Сохранение...' : dialogMode === 'create' ? 'Создать' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationsManager;
